import { Injectable } from '@angular/core';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypesService } from './object-types.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { first } from 'rxjs/operators';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

@Injectable()
export class PageTitleService {
    private textsLoaded$: Promise<void>;

    constructor(
        private sessionService: SessionService,
        private appTextService: AppTextService,
        private objectTypesService: ObjectTypesService,
        private stateService: FiStateService,
        private customerDataService: FiCustomerDataService,
    ) {
        this.textsLoaded$ = new Promise<void>((resolve, reject) => {
            appTextService.onInitialize$.pipe(first()).subscribe((language) => resolve());
        });
    }

    setTitle(title: string, prefix?: string) {
        const customerName = this.customerDataService.customerData.DisplayName;
        document.title = (prefix ? prefix + ' ' : '') + customerName + ' ' + title;
    }

    setTitleByTextId(textId: string) {
        this.textsLoaded$.then(() => {
            const title = this.appTextService.get(textId);
            this.setTitle(title);
        });
    }

    setTitleByObjectType(objectType: ObjectTypes, isPlural: boolean = true) {
        this.textsLoaded$.then(() => {
            const title = this.objectTypesService.GetNameByObjectType(objectType, isPlural);
            this.setTitle(title);
        });
    }

    setTitleForList(objectType: ObjectTypes) {
        this.textsLoaded$.then(() => {
            const title = this.objectTypesService.GetNameByObjectType(objectType, true);
            this.setTitle(title);
        });
    }

    setTitleForNewItem(objectType: ObjectTypes) {
        this.textsLoaded$.then(() => {
            let textId = this.objectTypesService.getTextIdForTitleCreate(objectType);
            if (textId) {
                this.setTitleByTextId(textId);
            } else {
                let title =
                    this.appTextService.get('common.new') +
                    ' ' +
                    this.objectTypesService.GetNameByObjectType(objectType).toLowerCase();
                this.setTitle(title);
            }
        });
    }

    setTitleByItem(item) {
        this.textsLoaded$.then(() => {
            if (typeof item === 'number') {
                this.setTitleForList(item); // items list, set title by object type
            } else {
                let objectIdName = this.objectTypesService.getObjectIdName(item);
                if (objectIdName) {
                    if (!objectIdName.id) {
                        // new item
                        this.setTitleForNewItem(objectIdName.objectType);
                    } else {
                        setTimeout(() => {
                            let title = objectIdName.name;
                            if (this.stateService.current.name.toLowerCase().indexOf('edit') !== -1) {
                                if (objectIdName.objectType == ObjectTypes.CheckList) {
                                    title = this.appTextService.get('checklist.edit_checklist') + ' - ' + title;
                                } else if (objectIdName.objectType == ObjectTypes.CheckListTemplate) {
                                    title = this.appTextService.get('checklist.edit_template') + ' - ' + title;
                                }
                            }
                            this.setTitle(title);
                        });
                    }
                }
            }
        });
    }

    setTitleByName(name: string, objectType: ObjectTypes) {
        if (name) {
            this.setTitle(name);
        } else {
            this.setTitleForNewItem(objectType);
        }
    }
}
